import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { FormField, TextareaField, TextField, ToggleField } from 'modules/Common/form/v2';
import { useSupplierBrandColor } from 'modules/FreeSupplier/useSupplierPublication';
import PortalStudioShell from 'modules/PublicCatalog/studio/PortalStudioShell';
import WidgetThemePicker from 'modules/PublicCatalog/studio/WidgetThemePicker';
import {
  ControlRow,
  CopyLinkButton,
  Group,
  TOGGLE_SIZING,
} from 'modules/PublicCatalog/studio/inspectorPrimitives';
import { INSPECTOR_TABS } from './inspectorTabs';

// The supplier studio's inspector rail: every portal setting, grouped into the
// three supplier tabs. The rail CHROME (tab strip, always-mounted panel host,
// sticky save footer) is the shared PortalStudioShell; this file owns only the
// supplier-specific panels + tab model, so the operator surface can build its
// own panels on the same shell.

function PublishPanel({ settings, publicUrl, onRegenerate }) {
  const { t } = useTranslation('freeSupplier');
  const { watch } = useFormContext();
  const enabled = watch('enabled');
  const includeFutureRevisions = watch('includeFutureRevisions');
  const passwordProtected = watch('passwordProtected');

  return (
    <>
      <Group first>
        <ControlRow
          emphasis
          title={t('portalStudio.portalIsPublicTitle', 'Portal is public')}
          hint={
            settings?.enabled
              ? settings?.passwordSet
                ? t(
                    'portalStudio.livePasswordProtectedHint',
                    'Live — protected by your portal password.',
                  )
                : t(
                    'portalStudio.liveAnyoneWithLinkHint',
                    'Live — anyone with the link can view it.',
                  )
              : t(
                  'portalStudio.currentlyOfflineHint',
                  'Currently offline. Turn this on and save to publish.',
                )
          }
        >
          <FormField
            name="enabled"
            className="!mb-0"
          >
            <ToggleField sizing={TOGGLE_SIZING} />
          </FormField>
        </ControlRow>

        {/* D39: enabling requires an explicit current/future inclusion choice,
            so this only makes sense while the portal is on. */}
        {enabled ? (
          <div className="mt-3">
            <ControlRow
              title={t('portalStudio.futureRevisionsTitle', 'Future revisions')}
              hint={
                includeFutureRevisions
                  ? t(
                      'portalStudio.futureRevisionsIncludedHint',
                      'New periods you publish later appear automatically.',
                    )
                  : t(
                      'portalStudio.futureRevisionsExcludedHint',
                      "Only what's published today. New periods stay private until you re-confirm here.",
                    )
              }
            >
              <FormField
                name="includeFutureRevisions"
                className="!mb-0"
              >
                <ToggleField sizing={TOGGLE_SIZING} />
              </FormField>
            </ControlRow>
          </div>
        ) : null}
      </Group>

      <Group
        stack
        title={t('portalStudio.publicLinkTitle', 'Public link')}
      >
        {publicUrl ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 min-w-0 text-[11px] text-slate-600 font-mono truncate rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5">
              {publicUrl}
            </span>
            <CopyLinkButton
              text={publicUrl}
              label={t('portalStudio.copyButton', 'Copy')}
            />
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 m-0">
            {t('portalStudio.saveOnceForLink', 'Save once to get your public link.')}
          </p>
        )}
        <FormField
          name="slug"
          label={t('portalStudio.linkNameLabel', 'Link name')}
          description={t(
            'portalStudio.linkNameDescription',
            'Lowercase letters, numbers and hyphens. Must be unique.',
          )}
        >
          <TextField />
        </FormField>
        {publicUrl ? (
          <CgRadixButton
            type="button"
            variant="ghost"
            size="sm"
            icon={faRotateRight}
            className="self-start"
            onClick={onRegenerate}
          >
            {t('portalStudio.regenerateLinkButton', 'Regenerate link')}
          </CgRadixButton>
        ) : null}
      </Group>

      <Group title={t('portalStudio.accessTitle', 'Access')}>
        <ControlRow
          title={t('portalStudio.passwordTitle', 'Password')}
          hint={t(
            'portalStudio.passwordHint',
            'The same password protects the portal page and the embedded widget.',
          )}
        >
          <FormField
            name="passwordProtected"
            className="!mb-0"
          >
            <ToggleField sizing={TOGGLE_SIZING} />
          </FormField>
        </ControlRow>
        {passwordProtected ? (
          <div className="mt-3">
            <FormField
              name="password"
              label={
                settings?.passwordSet
                  ? t('portalStudio.newPasswordLabel', 'New password')
                  : t('portalStudio.passwordLabel', 'Password')
              }
              description={
                settings?.passwordSet
                  ? t(
                      'portalStudio.passwordSetDescription',
                      'Leave blank to keep the current password.',
                    )
                  : t(
                      'portalStudio.passwordUnsetDescription',
                      'Visitors enter this before viewing either surface.',
                    )
              }
            >
              <TextField
                type="password"
                placeholder={
                  settings?.passwordSet
                    ? t('portalStudio.keepCurrentPasswordPlaceholder', 'Keep current password')
                    : t('portalStudio.setPasswordPlaceholder', 'Set a password')
                }
              />
            </FormField>
          </div>
        ) : null}
        <p className="text-[11px] text-slate-400 m-0 mt-1 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faLock} />
          {t('portalStudio.embedCodeNoPasswordNote', 'Embed code never contains the password.')}
        </p>
      </Group>
    </>
  );
}

// `seriesRows` arrives display-ready from the page (form key, installation name
// and all): the page owns the publication view-model — this panel only renders
// it, so it needs neither the fingerprint→form-key mapper nor the installation
// docs to look names up itself.
function ContentPanel({ seriesRows }) {
  const { t } = useTranslation('freeSupplier');
  return (
    <>
      <Group
        first
        title={
          seriesRows.length
            ? t('portalStudio.publishedDataTitleWithCount', 'Published data ({{count}})', {
                count: seriesRows.length,
              })
            : t('portalStudio.publishedDataTitle', 'Published data')
        }
      >
        {seriesRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center">
            <p className="text-xs text-slate-500 m-0 mb-1">
              {t('portalStudio.noPublishedFactorsYet', 'No published factors yet.')}
            </p>
            <Link
              to="/supplier/emission-factors"
              className="text-[11px] font-semibold text-primaryColor no-underline hover:underline"
            >
              {t('portalStudio.addEmissionsDataLink', 'Add emissions data →')}
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 -mt-1">
            {seriesRows.map((row) => (
              <div
                key={row.formKey}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  {/* A div, not a p: CgCnGoodHoverText renders a div, and a div
                      inside a p is invalid nesting (React logs
                      validateDOMNesting and the browser closes the p early). */}
                  <div className="text-xs text-slate-700 flex items-center gap-1.5 min-w-0">
                    <CgCnGoodHoverText
                      cnCode={row.cnCode}
                      useAutonomousData
                      variant="plain"
                    />
                    {row.productVariant ? (
                      <span className="truncate text-slate-500">· {row.productVariant}</span>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-slate-400 m-0">
                    {row.installationName} ·{' '}
                    {t('portalStudio.revisionSuffix', 'revision {{number}}', {
                      number: row.revisionNumber,
                    })}
                  </p>
                </div>
                <FormField
                  name={`included.${row.formKey}`}
                  className="!mb-0 shrink-0"
                >
                  <ToggleField sizing={TOGGLE_SIZING} />
                </FormField>
              </div>
            ))}
          </div>
        )}
      </Group>

      <Group
        stack
        title={t('portalStudio.companyProfileTitle', 'Company profile')}
      >
        <FormField
          name="profileText"
          label={t('portalStudio.aboutYourCompanyLabel', 'About your company')}
        >
          <TextareaField
            rows={4}
            placeholder={t(
              'portalStudio.profileTextPlaceholder',
              'A short description shown at the top of your CBAM Portal',
            )}
          />
        </FormField>
        <FormField
          name="websiteUrl"
          label={t('portalStudio.websiteLabel', 'Website')}
        >
          <TextField placeholder="https://your-company.com" />
        </FormField>
        <FormField
          name="contactEmail"
          label={t('portalStudio.publicContactEmailLabel', 'Public contact email')}
        >
          <TextField
            type="email"
            placeholder="sustainability@your-company.com"
          />
        </FormField>
      </Group>
    </>
  );
}

// The brand color is NOT part of this form: it persists on its own the moment
// Apply is pressed. So it owns its own state here rather than being brokered
// through the page as three props the page never reads — the same way
// EmbedWidgetPage calls this hook beside its own copy of the picker. The hook
// reads the shared publication query (one cache entry, no extra fetch).
function AppearancePanel() {
  const { t } = useTranslation('freeSupplier');
  const { effectiveColor, persistColor, saving } = useSupplierBrandColor();
  return (
    <>
      <Group first>
        <ControlRow
          title={t('portalStudio.brandColorTitle', 'Brand color')}
          hint={t(
            'portalStudio.brandColorHint',
            'Themes your public portal page and the embedded widget. Applies as soon as you press Apply — no save needed.',
          )}
        >
          <WidgetThemePicker
            value={effectiveColor}
            saving={saving}
            onApply={persistColor}
          />
        </ControlRow>
      </Group>

      <Group
        stack
        title={t('portalStudio.brandingTitle', 'Branding')}
      >
        <FormField
          name="logoUrl"
          label={t('portalStudio.logoUrlLabel', 'Logo URL')}
        >
          <TextField placeholder="https://your-company.com/logo.png" />
        </FormField>
        <FormField
          name="labelsText"
          label={t('portalStudio.publicLabelsLabel', 'Public labels')}
          description={t(
            'portalStudio.publicLabelsDescription',
            'Comma-separated badges shown beside your company name.',
          )}
        >
          <TextField
            placeholder={t('portalStudio.publicLabelsPlaceholder', 'Verified, Low-carbon products')}
          />
        </FormField>
      </Group>
    </>
  );
}

export default function PortalStudioInspector({
  settings,
  publicUrl,
  seriesRows,
  activeTab,
  onTabChange,
  onRegenerate,
  saving,
  saved,
}) {
  const panels = {
    publish: (
      <PublishPanel
        settings={settings}
        publicUrl={publicUrl}
        onRegenerate={onRegenerate}
      />
    ),
    content: <ContentPanel seriesRows={seriesRows} />,
    appearance: <AppearancePanel />,
  };

  return (
    <PortalStudioShell
      tabs={INSPECTOR_TABS}
      panels={panels}
      activeTab={activeTab}
      onTabChange={onTabChange}
      saving={saving}
      saved={saved}
    />
  );
}
